import { registerListResourceWithStore } from '@/services/GenericListResourceService'
import store from '@/store'

import Dashboard from '@/modules/dashboard/Dashboard.vue'

import RouterPassThrough from '@/components/RouterPassThrough.vue'
import FormsPassThrough from '@/modules/forms/FormsPassThrough.vue'
import attendanceManageRoutes from '@/modules/attendance/routes'
import systemOpsManageRoutes from '@/modules/systemops/routes'
import payrollManageRoutes from '@/modules/payroll/routes'
import scheduleManageRoutes from '@/modules/schedule/routes'
import timeOffManageRoutes from '@/modules/timeoff/routes'


import billingRoutes from '@/modules/billing/routes'
import costingRoutes from '@/modules/costing/routes'
import deviceRoutes from '@/modules/devices/routes'
import disciplineSettingsRoutes from '@/modules/discipline/routes'
import integrationsRoutes from '@/modules/integrations/routes'
import orgRoutes from '@/modules/organization/routes'
import timeOffSettingsRoutes from '@/modules/timeoff/settingsRedirectRoute'

import userRoutes from '@/modules/users/routes'

registerListResourceWithStore('memorizedReports', 'reports/templates', store)

const items = [
  {
    name: 'dashboard',
    path: 'dashboard',
    // Called Overview everywhere people see it (sidebar, breadcrumbs, tour); Dashboard is the
    // Classic console's name for it.
    label: 'Overview',
    searchAliases: ['Dashboard'],
    icon: 'tachometer-alt',
    description: 'View dashboard overview of key metrics.',
    component: Dashboard,
    // We keep alive dashboard, because its state is expensive to compute.
    // TODO: We should probably instead store its state in vuex.
    keepAlive: true
  },
  attendanceManageRoutes,
  {
    name: 'forms',
    path: 'forms',
    label: 'Forms',
    icon: 'file-alt',
    description: 'Manage Forms.',
    requireFeature: 'form',
    // edit_settings is used until forms have a dedicated management permission.
    requirePerm: 'edit_settings',
    breadcrumbLeaf: true,
    component: FormsPassThrough,
    children: [
      {
        name: 'forms-list',
        label: 'Manage Forms',
        title: 'Manage Forms',
        icon: 'file-alt',
        path: '',
        component: () => import('@/modules/forms/ManageForms.vue')
      },
      {
        name: 'form-builder',
        label: 'Form Builder',
        title: 'Form Builder',
        icon: 'file-alt',
        path: 'form-builder/:view?',
        component: () => import('@/modules/forms/BuildForm.vue')
      },
      {
        name: 'form-submission',
        label: 'Form Submission',
        title: 'Form Submission',
        icon: 'file-alt',
        path: 'form-submission/:view?/:submissionId?',
        component: () => import('@/modules/forms/SubmissionWrapper.vue')
      },
      {
        name: 'forms-view',
        label: 'Form view',
        title: 'Form view',
        icon: 'file-alt',
        path: 'view/:view?',
        component: () => import('@/modules/forms/FormView.vue')
      },
    ]
  },
  scheduleManageRoutes,
  payrollManageRoutes,
  timeOffManageRoutes,
  systemOpsManageRoutes,
  costingRoutes,
  deviceRoutes,
  userRoutes,
  {
    name: 'reports',
    label: 'Reports',
    path: 'reports',
    props: true,
    icon: 'chart-line',
    description: 'Run various reports.',
    component: () => import('@/modules/reports-center/ReportsCatalog.vue'),
    requirePerm: 'view_reports',
    children: [
      {
        name: 'reports-attendance',
        navLabel: 'Attendance Reports',
        navIcon: 'Clock',
        label: 'Attendance',
        path: 'attendance',
        component: RouterPassThrough,
        children: [
          {
            name: 'clock-logs-report',
            navLabel: 'Clock Logs Report',
            navIcon: 'History',
            label: 'Clock Logs',
            link: 'clock-logs',
            path: 'clock-logs/:view?',
            icon: 'history',
            description: 'View clock logs for selected date range.',
            component: () => import('@/modules/attendance/reports/ClockLogReport.vue')
          },
          {
            name: 'raw-punches-report',
            navLabel: 'Raw Punches Report',
            navIcon: 'Fingerprint',
            label: 'Raw Punches',
            link: 'punches',
            path: 'punches/:view?',
            icon: 'columns',
            description: 'View worker punches, with advanced customization options.',
            component: () => import('@/modules/attendance/reports/RawPunchReport.vue')
          },
          {
            name: 'raw-punch-totals-report',
            navIcon: 'Sigma',
            label: 'Raw Punch Totals',
            link: 'raw-punch-totals',
            path: 'raw-punch-totals/:view?',
            icon: 'table',
            description: 'View raw punch totals.',
            component: () => import('@/modules/attendance/reports/RawPunchTotalsReport.vue')
          },
          {
            name: 'roll-call',
            navIcon: 'Megaphone',
            label: 'Roll Call',
            link: 'rollcall',
            path: 'rollcall/:view?',
            props: true,
            icon: 'bullhorn',
            description: 'View workers currently clocked in or not.',
            component: () => import('@/modules/attendance/reports/RollCallReport.vue')
          },
          {
            name: 'tardy',
            navLabel: 'Tardy Report',
            navIcon: 'AlarmClock',
            label: 'Tardy',
            link: 'tardy',
            path: 'tardy/:view?',
            props: true,
            icon: 'bed',
            description: 'View workers who clocked IN late to work for selected date range, and total tardy hours.',
            component: () => import('@/modules/attendance/reports/TardyReport.vue')
          },
          {
            name: 'timecards-report',
            navLabel: 'Time Cards Report',
            navIcon: 'Table',
            label: 'Time Cards',
            link: 'timecards',
            path: 'timecards/:view?',
            icon: 'table',
            description: 'View worker work hours, by day.',
            component: () => import('@/modules/attendance/reports/TimeCardReport.vue')
          },
          {
            name: 'total-report',
            navLabel: 'Totals Report',
            navIcon: 'Calculator',
            label: 'Totals',
            link: 'totals',
            path: 'totals/:view?',
            icon: 'money-check-dollar',
            description: 'View hour and wage totals, with various grouping options.',
            component: () => import('@/modules/reports-center/total/TotalReport.vue')
          },
        ]
      },
      {
        name: 'reports-schedule',
        navLabel: 'Scheduling Reports',
        navIcon: 'Calendar',
        label: 'Scheduling',
        path: 'schedule',
        component: RouterPassThrough,
        children: [
          {
            name: 'schedule-report',
            navLabel: 'Schedule Report',
            navIcon: 'CalendarDays',
            label: 'Schedule',
            link: 'schedule',
            path: 'schedule/:view?',
            icon: 'calendar-alt',
            description: 'View worker shift schedule, by day.',
            component: () => import('@/modules/schedule/reports/ScheduleReport.vue'),
            requirePerm: 'manage_schedule',
            requireGetter: 'scheduleEnabled'
          },
          {
            name: 'schedule-budget-report',
            navLabel: 'Labor Budget Report',
            navIcon: 'Scale',
            label: 'Labor Budget',
            link: 'budget',
            path: 'budget/:view?',
            icon: 'scale-balanced',
            description: 'Compare budget, scheduled and actual hours by schedule group.',
            component: () => import('@/modules/schedule/reports/ScheduleBudgetReport.vue'),
            requirePerm: 'manage_schedule',
            requireFeature: 'schedule_controls',
            requireGetter: 'scheduleEnabled'
          },
          {
            name: 'shiftcards-report',
            navIcon: 'CalendarRange',
            label: 'Shift Summaries',
            link: 'shiftcards',
            path: 'shiftcards/:view?',
            icon: 'table',
            description: 'View worker shift summaries.',
            component: () => import('@/modules/schedule/reports/ShiftCardReport.vue'),
            requireFeature: 'schedule',
            requireGetter: 'scheduleEnabled'
          },
        ]
      },
      {
        name: 'reports-hr',
        navLabel: 'HR & Staff Reports',
        navIcon: 'Users',
        label: 'HR & Staff',
        path: 'hr',
        component: RouterPassThrough,
        children: [
          {
            name: 'absent',
            navLabel: 'Absent Report',
            navIcon: 'UserX',
            label: 'Absent',
            link: 'absent',
            path: 'absent/:view?',
            props: true,
            icon: 'plane',
            description: 'View absentees for selected date range.',
            component: () => import('@/modules/users/reports/AbsentReport.vue')
          },
          {
            name: 'absent-totals',
            navIcon: 'ChartPie',
            label: 'Absent Totals',
            link: 'absent-totals',
            path: 'absent-totals/:view?',
            props: true,
            icon: 'jet-fighter',
            description: 'View absence analytics in a pivot table.',
            component: () => import('@/modules/users/reports/AbsentTotalsReport.vue')
          },
          {
            name: 'briefings',
            navLabel: 'Briefings Report',
            navIcon: 'Presentation',
            label: 'Briefings',
            link: 'briefings',
            path: 'briefings/:view?',
            icon: 'briefcase',
            description: 'View briefing reports.',
            component: () => import('@/modules/users/reports/BriefingReport.vue'),
            requireGetter: ['adminAllowAllLabels', 'enableBriefings']
          },
          {
            name: 'credential-report',
            navLabel: 'Credentials Report',
            navIcon: 'IdCard',
            label: 'Credentials',
            link: 'credentials',
            path: 'credentials/:view?',
            icon: 'id-badge',
            description: 'View user credentials, with features to track invalid and upcoming expiring credentials.',
            component: () => import('@/modules/users/reports/CredentialReport.vue'),
            requireGetter: 'credentialsEnabled',
            require: (state, getters) => getters.canAccessEmployeeSensitiveField('credential')
          },
          {
            name: 'credential-roll-callreport',
            navIcon: 'BadgeCheck',
            label: 'Credential Roll Call',
            link: 'credential-roll-call',
            path: 'credential-roll-call/:view?',
            icon: 'badge-check',
            description: 'View valid typed user credentials for workers who are currently clocked IN.',
            component: () => import('@/modules/users/reports/CredentialRollCallReport.vue'),
            requireGetter: 'credentialsEnabled',
            require: (state, getters) => getters.canAccessEmployeeSensitiveField('credential')
          },
          {
            name: 'timeoff-report',
            navLabel: 'Time Off Report',
            navIcon: 'Plane',
            label: 'Time Off',
            link: 'timeoff',
            path: 'timeoff/:view?',
            icon: 'umbrella-beach',
            description: 'View time off entries.',
            component: () => import('@/modules/timeoff/reports/TimeOffReport.vue'),
            requireFeature: 'pto',
            requireGetter: 'timeOffEnabled'
          },
          {
            name: 'time-off-balance-report',
            navIcon: 'PiggyBank',
            label: 'Time Off Balances',
            link: 'time-off-balance',
            path: 'time-off-balance/:view?',
            icon: 'balance-scale',
            description: 'View time off balances.',
            component: () => import('@/modules/timeoff/reports/TimeOffBalanceReport.vue'),
            requireFeature: 'pto',
            requireGetter: 'timeOffAccrualEnabled'
          },
          {
            name: 'time-off-totals-report',
            navIcon: 'Sigma',
            label: 'Time Off Totals',
            link: 'time-off-totals',
            path: 'time-off-totals/:view?',
            icon: 'island-tropical',
            description: 'View time off totals.',
            component: () => import('@/modules/timeoff/reports/TimeOffTotalsReport.vue'),
            requireFeature: 'pto',
            requireGetter: 'timeOffEnabled'
          },
        ]
      },
      {
        name: 'reports-payroll',
        navLabel: 'Payroll Reports',
        navIcon: 'CreditCard',
        label: 'Payroll',
        path: 'payroll',
        component: RouterPassThrough,
        children: [
          {
            name: 'net-pay-analysis-report',
            navIcon: 'TrendingUp',
            label: 'Net Pay Analysis',
            link: 'net-pay-analysis',
            path: 'net-pay-analysis/:view?',
            props: true,
            icon: 'analytics',
            description: 'Analyze net pay, by component and/or per worker.',
            component: () => import('@/modules/payroll/reports/NetPayAnalysisReport.vue'),
            requireFeature: 'netpay',
            requirePerm: 'manage_payroll',
            requireGetter: 'netPayEnabled',
          },
          {
            name: 'net-pay-annualization-report',
            navIcon: 'FileSpreadsheet',
            label: 'Net Pay Annualization',
            link: 'net-pay-annualization',
            path: 'net-pay-annualization/:view?',
            props: true,
            icon: 'money-bill-transfer',
            description: 'Run annualization (alphalist) report for year to compute tax return.',
            component: () => import('@/modules/payroll/reports/NetPayAnnualizationReport.vue'),
            requireFeature: 'netpay',
            requirePerm: 'manage_payroll',
            requireGetter: 'netPayEnabled',
          },
          {
            name: 'net-pay-month13-report',
            navIcon: 'Gift',
            label: 'Net Pay 13th Month',
            link: 'net-pay-month13',
            path: 'net-pay-month13/:view?',
            props: true,
            icon: 'money-bill-trend-up',
            description: 'View 13th month pay summary, either already paid or expected.',
            component: () => import('@/modules/payroll/reports/NetPayMonth13Report.vue'),
            requireFeature: 'netpay',
            requirePerm: 'manage_payroll',
            requireGetter: 'netPayEnabled'
          },
          {
            name: 'payroll-report',
            navLabel: 'Payroll Report',
            navIcon: 'Banknote',
            label: 'Payroll',
            link: 'payroll',
            path: 'payroll/:view?',
            icon: 'money-bill',
            description: 'View worker payroll, with custom export and template support.',
            component: () => import('@/modules/payroll/reports/PayrollReport.vue'),
            requireFeature: 'integration'
          },
          {
            name: 'pay-run-analysis-report',
            navIcon: 'ChartLine',
            label: 'Pay Run Analysis',
            link: 'pay-run-analysis',
            path: 'pay-run-analysis/:view?',
            props: true,
            icon: 'analytics',
            description: 'Analyze pay runs, by total and/or per worker.',
            component: () => import('@/modules/payroll/reports/PayRunAnalysisReport.vue'),
            requireFeature: 'pay_run',
            requirePerm: 'manage_payroll',
            requireGetter: 'payRunEnabled',
          },
          {
            name: 'pay-run-code-analysis-report',
            navIcon: 'ChartPie',
            label: 'Pay Run Code Analysis',
            link: 'pay-run-code-analysis',
            path: 'pay-run-code-analysis/:view?',
            props: true,
            icon: 'analytics',
            description: 'Analyze pay runs, by pay and/or time off code.',
            component: () => import('@/modules/payroll/reports/PayRunCodeAnalysisReport.vue'),
            requireFeature: 'pay_run',
            requirePerm: 'manage_payroll',
            requireGetter: 'payRunEnabled',
          },
          {
            name: 'pay-run-labor-analysis-report',
            navIcon: 'HardHat',
            label: 'Pay Run Labor Analysis',
            link: 'pay-run-labor-analysis',
            path: 'pay-run-labor-analysis/:view?',
            props: true,
            icon: 'analytics',
            description: 'Analyze pay runs, by entry date and/or job.',
            component: () => import('@/modules/payroll/reports/PayRunLaborAnalysisReport.vue'),
            requireFeature: 'pay_run',
            requirePerm: 'manage_payroll',
            requireGetter: 'payRunEnabled',
          },
          {
            name: 'salary-report',
            navLabel: 'Salary Report',
            navIcon: 'Wallet',
            label: 'Salary',
            link: 'salary',
            path: 'salary/:view?',
            icon: 'money-check',
            description: 'View worker salary summary.',
            component: () => import('@/modules/payroll/reports/SalaryReport.vue'),
            requireGetter: ['canViewWorkerPay', 'payrollEnabled', 'scheduleEnabled']
          },
          {
            name: 'time-off-monetization-report',
            navIcon: 'HandCoins',
            label: 'Time Off Monetization',
            link: 'time-off-monetization',
            path: 'time-off-monetization/:view?',
            icon: 'money-bill-transfer',
            description: 'View time off monetization.',
            component: () => import('@/modules/payroll/reports/TimeOffMonetizationReport.vue'),
            requireFeature: 'netpay',
            requirePerm: 'manage_payroll',
            requireGetter: 'netPaySettings/timeOffMonetizeEnabled',
            guardInitializer: async store => {
              if (store.getters.payrollEnabled && store.getters.netPayEnabled) {
                await store.dispatch('netPaySettings/load')
              }
            }
          },
        ]
      },
      {
        name: 'reports-costing',
        navLabel: 'Job Costing Reports',
        navIcon: 'Coins',
        label: 'Job Costing',
        path: 'costing',
        component: RouterPassThrough,
        children: [
          {
            name: 'job-transfer-report',
            navIcon: 'ArrowLeftRight',
            label: 'Job Transfers',
            link: 'job-transfers',
            path: 'job-transfers/:view?',
            icon: 'sign-in-alt',
            description: 'View worker time cards, in job-transfer style.',
            component: () => import('@/modules/costing/reports/JobTransferReport.vue'),
            requireGetter: 'costingEnabled'
          },
        ]
      },
      {
        name: 'reports-audit',
        navLabel: 'Audit & Safety Reports',
        navIcon: 'ShieldCheck',
        label: 'Audit & Safety',
        path: 'audit',
        component: RouterPassThrough,
        children: [
          {
            name: 'audit',
            navLabel: 'Audit Log',
            navIcon: 'FileSearch',
            label: 'Audit',
            link: 'audit',
            path: 'audit/:view?',
            icon: 'user-secret',
            description: 'View audit logs.',
            component: () => import('@/modules/reports-center/audit/AuditReport.vue'),
            requirePerm: 'view_audit'
          },
          {
            name: 'audit-time-entry',
            navLabel: 'Time Entry Audit',
            navIcon: 'FileClock',
            label: 'Audit Time',
            link: 'audit-time-entry',
            path: 'audit-time-entry/:view?',
            icon: 'user-secret',
            description: 'View edited time entries.',
            component: () => import('@/modules/reports-center/audit/AuditTimeEntryReport.vue'),
            requirePerm: 'view_audit'
          },
        ]
      },
      {
        // Route name stays 'memorized' -- it is an internal identifier wired through
        // ReportsCatalog and saved links, and renaming it would break bookmarks for no gain.
        // Only what people read changed.
        name: 'memorized',
        navIcon: 'Bookmark',
        label: 'My Saved Views',
        path: 'memorized/:category?',
        icon: 'lightbulb',
        description: 'View and run your saved report views.',
        searchAliases: ['Memorized Reports'],
        component: () => import('@/modules/reports-center/memorizedreports/TheMemorizedReports.vue')
      },
      {
        name: 'reports-delivery',
        navLabel: 'Report Delivery Schedules',
        navIcon: 'Send',
        label: 'Delivery Schedules',
        path: 'delivery/:category?',
        icon: 'envelope',
        description: 'View and edit scheduled reports.',
        searchAliases: ['Scheduled Reports'],
        component: () => import('@/modules/reports-center/memorizedreports/TheMemorizedReports.vue')
      }
    ]
  },
  {
    name: 'settings',
    label: 'Settings',
    path: 'settings',
    props: true,
    icon: 'cog',
    description: 'View and edit your account settings.',
    component: () => import('@/modules/settings/SettingsWrapper.vue'),
    requireActive: false,
    children: [
      disciplineSettingsRoutes,
      billingRoutes,
      {
        name: 'settings-costing-redirect',
        label: 'Costing',
        path: 'costing',
        icon: 'industry',
        description: 'Standardize how labor costs are captured and attributed.',
        requirePerm: 'edit_jobs',
        requireFeature: 'job_costing',
        require: (state, getters) => {
          return state.permissions.includes('edit_settings') || getters.costingEnabled
        },
        redirect: { name: 'costing-defaults' }
      },
      {
        name: 'settings-devices-redirect',
        label: 'Devices',
        path: 'devices',
        icon: 'mobile-alt',
        description: 'Control the clock-in experience, location rules, and device behavior.',
        requirePerm: 'edit_clocks',
        redirect: { name: 'device-defaults' }
      },
      integrationsRoutes,
      orgRoutes,
      {
        name: 'settings-payroll-redirect',
        label: 'Payroll',
        path: 'payroll',
        icon: 'money-bill-wave',
        description: 'Define how time and earnings flow into payroll outcomes.',
        requirePerm: 'manage_payroll',
        requireFeature: 'pay_run',
        redirect: { name: 'payroll-defaults' }
      },
      {
        name: 'settings-shifts-redirect',
        label: 'Shifts',
        path: 'shifts',
        icon: 'calendar-check',
        description: 'Shape scheduling and time-calculation rules for daily operations.',
        requirePerm: 'manage_schedule',
        requireGetter: 'hasScheduleFeature',
        redirect: { name: 'shift-defaults' }
      },
      timeOffSettingsRoutes,
      {
        name: 'settings-users-redirect',
        label: 'Users',
        path: 'users',
        icon: 'users',
        description: 'Set up your workforce with consistent access and profile standards.',
        requirePerm: 'edit_employees',
        redirect: { name: 'employee-defaults' }
      }
    ]
  },
  {
    name: 'byxbi',
    label: 'Byxbi',
    path: 'byxbi',
    icon: 'people-roof',
    description: 'Byxbi-specific management.',
    component: () => import('@/modules/byxbi/Byxbi.vue'),
    // Disabled accounts must remain manageable, in order to be re-enabled.
    requireActive: false,
    requireGetter: 'canManageByxbi'
  }
]

function createRouteItem (item, parentPath = '') {
  const currentPath = parentPath ? `${parentPath}/${item.path}` : item.path
  const route = {
    name: item.name,
    path: item.path,
    redirect: item.redirect,
    beforeEnter: item.beforeEnter,
    component: item.component,
    props: item.props,
    children: item.children && item.children.map(child => createRouteItem(child, currentPath)),
    meta: {
      nav: item.nav !== false, // distinguish navigation route vs not (e.g., error, sign-up)
      icon: item.icon,
      label: item.label,
      title: item.title,
      link: item.link || item.path,
      badge: item.badge,
      description: item.description,
      requireActive: item.requireActive,
      requireFeature: item.requireFeature,
      requirePerm: item.requirePerm,
      requireGetter: item.requireGetter,
      require: item.require,
      guardInitializer: item.guardInitializer,
      keepAlive: item.keepAlive,
      breadcrumbLeaf: item.breadcrumbLeaf,
      // Former names for this page, so the header search still finds it for anyone who learned
      // it under the old vocabulary. Named searchAliases rather than aliases to keep it clear
      // of vue-router's own `alias`, which is a URL path and set further down this function.
      searchAliases: item.searchAliases,
      // Sidebar defaults for this page (see components/Sidebar/navCatalog.ts): the name it goes by
      // out of context when `label` alone is ambiguous ("Time Card Editor" for the tab labelled
      // "Editor"), and the Lucide icon and tile color it gets when added to someone's sidebar.
      navLabel: item.navLabel,
      navIcon: item.navIcon,
      navColor: item.navColor
    }
  }

  // If this is a report leaf route nested under category routes (like reports/attendance/clock-logs)
  // we add a flat alias to support direct URLs and saved views
  if (parentPath && parentPath.startsWith('reports/')) {
    route.alias = `/orgs/:orgId/reports/${item.path}`
  }

  return route
}

const routeItems = items.map(item => createRouteItem(item))

export default {
  routeItems
}
